/**
 * Auto-slide extension for slideshow-component (Desktop) and circle-carousel (Mobile)
 * This script adds automatic sliding functionality to both carousel types
 */

(function() {
  'use strict';

  console.log('🎠 Slideshow Auto-slide script loaded (v2 - Desktop + Mobile)');

  // Configuration
  const CONFIG = {
    autoplayInterval: 3000, // 3 seconds between slides
    pauseOnHover: true,
    pauseOnInteraction: true,
    resumeDelay: 1000, // Resume after 1 second of no interaction
  };

  console.log('⚙️ Config:', CONFIG);

  /**
   * Initialize auto-slide for desktop slideshow component
   * @param {HTMLElement} slideshow - The slideshow-component element
   */
  function initDesktopAutoSlide(slideshow) {
    console.log('🖥️ Initializing DESKTOP slideshow:', slideshow);

    // SKIP if inside infinite scroll wrapper
    if (slideshow.closest('.infinite-scroll-wrapper')) {
      console.log('🚫 SKIPPING - Inside infinite scroll wrapper');
      return;
    }

    const slides = slideshow.querySelectorAll('slideshow-slide:not([hidden])');
    console.log('📊 Found slides:', slides.length);
    
    if (slides.length <= 1) {
      console.log('⚠️ Not enough slides (need more than 1), skipping autoplay');
      return;
    }

    if (slideshow.hasAttribute('data-autoplay-initialized')) {
      console.log('⚠️ Slideshow already initialized, skipping');
      return;
    }
    
    console.log('✅ Initializing autoplay for desktop slideshow');
    slideshow.setAttribute('data-autoplay-initialized', 'true');

    let autoplayTimer = null;
    let resumeTimer = null;
    let isPaused = false;

    function startAutoplay() {
      if (isPaused || autoplayTimer) {
        console.log('⏸️ Desktop autoplay not starting - isPaused:', isPaused, 'autoplayTimer exists:', !!autoplayTimer);
        return;
      }

      console.log('▶️ Starting DESKTOP autoplay with interval:', CONFIG.autoplayInterval + 'ms');

      autoplayTimer = setInterval(() => {
        if (document.hidden) {
          console.log('👁️ Page hidden, skipping slide');
          return;
        }
        
        if (CONFIG.pauseOnHover && slideshow.matches(':hover')) {
          console.log('🖱️ Mouse hovering, skipping slide');
          return;
        }

        console.log('➡️ Moving to next slide...');

        const nextButton = slideshow.querySelector('.slideshow-control--next');
        console.log('🔘 Next button found:', !!nextButton, 'disabled:', nextButton?.disabled);
        
        if (nextButton && !nextButton.disabled) {
          console.log('✅ Clicking next button');
          nextButton.click();
        } else {
          console.log('🔄 At end, looping back to start');
          
          if (typeof slideshow.select === 'function') {
            console.log('✅ Using slideshow.select(0) method');
            slideshow.select(0);
            return;
          }
          
          const prevButton = slideshow.querySelector('.slideshow-control--previous');
          if (prevButton) {
            console.log('✅ Using previous button to loop back');
            const slides = slideshow.querySelectorAll('slideshow-slide:not([hidden])');
            const currentIndex = Array.from(slides).findIndex(s => s.getAttribute('aria-hidden') === 'false');
            console.log('Current slide index:', currentIndex, 'Total slides:', slides.length);
            
            for (let i = 0; i < currentIndex; i++) {
              prevButton.click();
            }
            return;
          }
          
          const scroller = slideshow.querySelector('slideshow-slides[ref="scroller"]');
          const firstSlide = slideshow.querySelector('slideshow-slide:not([hidden])');
          
          if (scroller && firstSlide) {
            console.log('✅ Scrolling to first slide directly');
            firstSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            return;
          }
          
          console.log('❌ All loop-back methods failed');
        }
      }, CONFIG.autoplayInterval);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        console.log('⏹️ Stopping DESKTOP autoplay');
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function pauseAutoplay() {
      console.log('⏸️ Pausing DESKTOP autoplay');
      stopAutoplay();
      
      if (resumeTimer) {
        clearTimeout(resumeTimer);
        resumeTimer = null;
      }
    }

    function resumeAutoplay() {
      if (isPaused) {
        console.log('⏸️ Not resuming - manually paused');
        return;
      }
      
      console.log('⏱️ Scheduling DESKTOP resume in', CONFIG.resumeDelay + 'ms');
      pauseAutoplay();
      
      resumeTimer = setTimeout(() => {
        console.log('▶️ Resuming DESKTOP autoplay after delay');
        startAutoplay();
      }, CONFIG.resumeDelay);
    }

    // Event listeners
    if (CONFIG.pauseOnHover) {
      console.log('🖱️ Setting up hover pause');
      slideshow.addEventListener('mouseenter', pauseAutoplay);
      slideshow.addEventListener('mouseleave', resumeAutoplay);
    }

    if (CONFIG.pauseOnInteraction) {
      console.log('👆 Setting up interaction pause');
      const controls = slideshow.querySelectorAll('.slideshow-control');
      console.log('🔘 Found control buttons:', controls.length);
      controls.forEach(control => {
        control.addEventListener('click', resumeAutoplay);
      });

      slideshow.addEventListener('mousedown', resumeAutoplay);
      slideshow.addEventListener('touchstart', resumeAutoplay);

      const thumbnails = slideshow.querySelectorAll('[role="tab"]');
      console.log('🎯 Found thumbnails/tabs:', thumbnails.length);
      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', resumeAutoplay);
      });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        console.log('👁️ Page hidden - pausing');
        pauseAutoplay();
      } else {
        console.log('👁️ Page visible - resuming');
        resumeAutoplay();
      }
    });

    slideshow.autoplayControl = {
      start: startAutoplay,
      stop: stopAutoplay,
      pause: () => {
        console.log('🎮 Manual pause triggered');
        isPaused = true;
        pauseAutoplay();
      },
      resume: () => {
        console.log('🎮 Manual resume triggered');
        isPaused = false;
        resumeAutoplay();
      },
      toggle: () => {
        if (isPaused) {
          slideshow.autoplayControl.resume();
        } else {
          slideshow.autoplayControl.pause();
        }
      }
    };

    console.log('🚀 Starting initial DESKTOP autoplay');
    startAutoplay();
  }

  /**
   * Initialize auto-slide for mobile circle carousel
   * @param {HTMLElement} carousel - The circle-carousel element
   */
  function initMobileAutoSlide(carousel) {
    console.log('📱 Initializing MOBILE carousel:', carousel);

    // SKIP if inside infinite scroll wrapper
    if (carousel.closest('.infinite-scroll-wrapper')) {
      console.log('🚫 SKIPPING - Inside infinite scroll wrapper');
      return;
    }

    const container = carousel.querySelector('.ai-circle-carousel-container-ar092aujqeeleqzu2saigenblock4acc26dcvfrw8');
    const items = carousel.querySelectorAll('.ai-circle-carousel-item-ar092aujqeeleqzu2saigenblock4acc26dcvfrw8');
    
    console.log('📊 Found items:', items.length);
    console.log('📦 Container:', !!container);
    
    if (!container || items.length <= 1) {
      console.log('⚠️ Not enough items or container not found, skipping autoplay');
      return;
    }

    if (carousel.hasAttribute('data-autoplay-initialized')) {
      console.log('⚠️ Carousel already initialized, skipping');
      return;
    }
    
    console.log('✅ Initializing autoplay for mobile carousel');
    carousel.setAttribute('data-autoplay-initialized', 'true');

    let autoplayTimer = null;
    let resumeTimer = null;
    let isPaused = false;
    let currentIndex = 0;

    function getItemWidth() {
      if (items.length > 0) {
        const itemStyle = window.getComputedStyle(items[0]);
        const itemWidth = items[0].offsetWidth;
        const marginRight = parseFloat(itemStyle.marginRight) || 0;
        const gap = 30; // From your CSS
        return itemWidth + gap;
      }
      return 100;
    }

    function scrollToIndex(index) {
      const itemWidth = getItemWidth();
      const scrollPosition = itemWidth * index;
      console.log('📍 Scrolling to index:', index, 'position:', scrollPosition);
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }

    function startAutoplay() {
      if (isPaused || autoplayTimer) {
        console.log('⏸️ Mobile autoplay not starting - isPaused:', isPaused, 'autoplayTimer exists:', !!autoplayTimer);
        return;
      }

      console.log('▶️ Starting MOBILE autoplay with interval:', CONFIG.autoplayInterval + 'ms');

      autoplayTimer = setInterval(() => {
        if (document.hidden) {
          console.log('👁️ Page hidden, skipping slide');
          return;
        }

        console.log('➡️ Moving to next item... Current index:', currentIndex);

        currentIndex++;
        
        if (currentIndex >= items.length) {
          console.log('🔄 At end, looping back to start');
          currentIndex = 0;
        }

        scrollToIndex(currentIndex);
      }, CONFIG.autoplayInterval);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        console.log('⏹️ Stopping MOBILE autoplay');
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function pauseAutoplay() {
      console.log('⏸️ Pausing MOBILE autoplay');
      stopAutoplay();
      
      if (resumeTimer) {
        clearTimeout(resumeTimer);
        resumeTimer = null;
      }
    }

    function resumeAutoplay() {
      if (isPaused) {
        console.log('⏸️ Not resuming - manually paused');
        return;
      }
      
      console.log('⏱️ Scheduling MOBILE resume in', CONFIG.resumeDelay + 'ms');
      pauseAutoplay();
      
      resumeTimer = setTimeout(() => {
        console.log('▶️ Resuming MOBILE autoplay after delay');
        startAutoplay();
      }, CONFIG.resumeDelay);
    }

    // Event listeners
    if (CONFIG.pauseOnInteraction) {
      console.log('👆 Setting up interaction pause');
      container.addEventListener('touchstart', resumeAutoplay);
      container.addEventListener('scroll', () => {
        // Update current index based on scroll position
        const itemWidth = getItemWidth();
        const scrollLeft = container.scrollLeft;
        currentIndex = Math.round(scrollLeft / itemWidth);
        console.log('📍 User scrolled, current index:', currentIndex);
      });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        console.log('👁️ Page hidden - pausing');
        pauseAutoplay();
      } else {
        console.log('👁️ Page visible - resuming');
        resumeAutoplay();
      }
    });

    carousel.autoplayControl = {
      start: startAutoplay,
      stop: stopAutoplay,
      pause: () => {
        console.log('🎮 Manual pause triggered');
        isPaused = true;
        pauseAutoplay();
      },
      resume: () => {
        console.log('🎮 Manual resume triggered');
        isPaused = false;
        resumeAutoplay();
      },
      toggle: () => {
        if (isPaused) {
          carousel.autoplayControl.resume();
        } else {
          carousel.autoplayControl.pause();
        }
      }
    };

    console.log('🚀 Starting initial MOBILE autoplay');
    startAutoplay();
  }

  /**
   * Initialize both desktop and mobile carousels
   */
  function initAllCarousels() {
    console.log('🔍 Initializing carousels...');
    
    // Desktop slideshow
    const desktopSection = document.querySelector('#shopify-section-template--24986512130322__collection_list_irTNeE');
    if (desktopSection) {
      console.log('✅ Desktop section found');
      const slideshow = desktopSection.querySelector('slideshow-component.resource-list__carousel');
      if (slideshow) {
        console.log('🎠 Desktop slideshow found, initializing...');
        setTimeout(() => initDesktopAutoSlide(slideshow), 100);
      } else {
        console.log('⚠️ No desktop slideshow found');
      }
    } else {
      console.log('⚠️ Desktop section not found');
    }
    
    // Mobile carousel
    const mobileSection = document.querySelector('#shopify-section-template--24986512130322__section_TLpRc7');
    if (mobileSection) {
      console.log('✅ Mobile section found');
      const carousel = mobileSection.querySelector('circle-carousel-ar092aujqeeleqzu2saigenblock4acc26dcvfrw8');
      if (carousel) {
        console.log('📱 Mobile carousel found, initializing...');
        setTimeout(() => initMobileAutoSlide(carousel), 100);
      } else {
        console.log('⚠️ No mobile carousel found');
      }
    } else {
      console.log('⚠️ Mobile section not found');
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    console.log('⏳ DOM still loading, waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', () => {
      console.log('✅ DOMContentLoaded fired, initializing...');
      initAllCarousels();
    });
  } else {
    console.log('✅ DOM already loaded, initializing immediately...');
    initAllCarousels();
  }

  // Watch for dynamically added carousels
  const observer = new MutationObserver((mutations) => {
    console.log('🔄 DOM mutation detected');
    
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Check for desktop slideshow
          if (node.matches && node.matches('slideshow-component.resource-list__carousel')) {
            const inDesktopSection = node.closest('#shopify-section-template--24986512130322__collection_list_irTNeE');
            const inInfiniteScroll = node.closest('.infinite-scroll-wrapper');
            if (inDesktopSection && !inInfiniteScroll) {
              console.log('✨ Desktop slideshow added to DOM');
              setTimeout(() => initDesktopAutoSlide(node), 100);
            }
          }
          
          // Check for mobile carousel
          if (node.matches && node.matches('circle-carousel-ar092aujqeeleqzu2saigenblock4acc26dcvfrw8')) {
            const inMobileSection = node.closest('#shopify-section-template--24986512130322__section_TLpRc7');
            const inInfiniteScroll = node.closest('.infinite-scroll-wrapper');
            if (inMobileSection && !inInfiniteScroll) {
              console.log('✨ Mobile carousel added to DOM');
              setTimeout(() => initMobileAutoSlide(node), 100);
            }
          }
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  console.log('👀 Mutation observer active');

})();

console.log('🏁 Slideshow Auto-slide script initialization complete');